/* import Avatar from 'assets/profileAvatars/Avatar.png'; */
import { Button, StatusBlock } from 'view/components';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import CircularProgress from '@mui/material/CircularProgress';
import { useContextSelector as useCS } from 'use-context-selector';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { CompanyLogoIcon } from 'assets';
import { useNavigate } from 'react-router';
import { useAuth } from 'view/pages/auth/hooks/useAuth';
import * as S from './styled';

export const getMatchScoreColor = (matchScoreParam: number) => {
  if (matchScoreParam > 0 && matchScoreParam <= 25) return '#BF2626';
  if (matchScoreParam > 25 && matchScoreParam <= 50) return '#FF993B';
  if (matchScoreParam > 50 && matchScoreParam <= 75) return '#E5C555';
  if (matchScoreParam > 75) return '#85C553';
  return 'transparent';
};

const JobPostsListItem = ({ candidate, queryAfterSubmit }) => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));
  const { isAuthenticated } = useAuth();

  const isShowMatchScore = useCS(GlobalConfigContext, globalConfigSelectors.isShowMatchScore);

  const getJobLocationDescription = (jobLocation: any) => {
    if (!jobLocation) return '';
    const locations: Array<string> = [];

    if (jobLocation.onSite) locations.push('On Site');
    if (jobLocation.remote) locations.push('Remote');
    if (jobLocation.hybrid) locations.push('Hybrid');

    return locations?.length > 0 ? `(${locations.join(', ')})` : '';
  };

  const getPositionTypes = (positionTypes: any) => {
    if (!positionTypes?.length) return 'No position types';
    return positionTypes
      .map((positionType: any) => {
        return positionType.name;
      })
      .join(', ');
  };

  const matchScore = candidate?.score;

  return (
    <S.CandidateListItem>
      <S.CandidateListItemHeading>
        <S.CandidateListItemHeadingInfo>
          <div style={{ width: '72px', height: '72px', position: 'relative' }}>
            {candidate.company?.logo?.link || candidate.company?.logoUrl ? (
              <img
                src={candidate.company?.logo?.link || candidate.company?.logoUrl}
                alt={candidate.company?.name}
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  objectFit: 'cover' as const,
                }}
              />
            ) : (
              <CompanyLogoIcon style={{ width: '72px', height: '72px', borderRadius: '50%' }} />
            )}
            {isShowMatchScore ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={matchScore}
                  style={{
                    width: '72px',
                    height: '72px',
                    color: getMatchScoreColor(matchScore),
                  }}
                />
              </div>
            ) : null}
          </div>

          <S.CandidateListItemHeadingInfoTexts>
            <S.CandidateListItemHeadingInfoTitle>
              {candidate.jobTitle || 'No Job Title'} <span>{candidate.username}</span>
            </S.CandidateListItemHeadingInfoTitle>
            <S.CandidateListItemHeadingInfoSalary>
              {!candidate.salaryMin && !candidate.salaryMax
                ? 'Salary not specified'
                : `$${candidate.salaryMin.toLocaleString()} - $${candidate.salaryMax.toLocaleString()}`}
            </S.CandidateListItemHeadingInfoSalary>
            {isShowMatchScore && matchScore > 0 ? (
              <S.CandidateListItemHeadingInfoSalary style={{ color: '#444444' }}>
                {matchScore}% match
              </S.CandidateListItemHeadingInfoSalary>
            ) : null}
          </S.CandidateListItemHeadingInfoTexts>
        </S.CandidateListItemHeadingInfo>
        {!matches && (
          <S.CandidateListItemHeadingButtons>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {(candidate?.application?.statusForCandidate ||
                candidate?.applicationStatus?.statusForCandidate) && (
                <StatusBlock
                  appStatus={
                    candidate?.application?.statusForCandidate ||
                    candidate?.applicationStatus?.statusForCandidate
                  }
                />
              )}
              <Button
                variant="outlined"
                text="View Job Post"
                style={{
                  padding: '10px 20px',
                  width: '160px',
                  minWidth: '160px',
                  height: '40px',
                  minHeight: '40px',
                  fontSize: '14px',
                }}
                onClick={() => {
                  if (isAuthenticated) {
                    navigate(
                      `/profile/view-job-post?id=${candidate.id}&matchScore=${isShowMatchScore && matchScore ? matchScore : 0}&query=${queryAfterSubmit ? `${encodeURIComponent(queryAfterSubmit)}` : ''}`
                    );
                  } else {
                    navigate(`/jobs/${candidate.id}`);
                  }
                }}
              />
            </div>
            <p>
              Updated on{' '}
              {new Date(candidate.updatedAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </S.CandidateListItemHeadingButtons>
        )}
      </S.CandidateListItemHeading>
      <S.Separator />
      <S.CandidateListItemMain>
        <S.CandidateListItemMainTitles>
          <p>Company Name</p>
          <p>Location</p>
          <p>Position Type</p>
        </S.CandidateListItemMainTitles>
        <S.CandidateListItemMainValues>
          <p>{candidate?.company?.name || candidate?.companyNameLegacy || 'No company name'}</p>
          <p>
            {candidate?.location?.name || 'No location'}{' '}
            <span>{getJobLocationDescription(candidate?.jobLocation)}</span>
          </p>
          <p> {getPositionTypes(candidate?.positionType)} </p>
        </S.CandidateListItemMainValues>
      </S.CandidateListItemMain>
      {matches && (
        <S.CandidateListItemHeadingButtons style={{ marginTop: '14px', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            <Button
              variant="outlined"
              text="View Job Post"
              style={{
                padding: '10px 20px',
                height: '40px',
                minHeight: '40px',
                fontSize: '14px',
              }}
              onClick={() => {
                if (isAuthenticated) {
                  navigate(
                    `/profile/view-job-post?id=${candidate.id}&matchScore=${isShowMatchScore && matchScore ? matchScore : 0}&query=${queryAfterSubmit ? `${encodeURIComponent(queryAfterSubmit)}` : ''}`
                  );
                } else {
                  navigate(`/jobs/${candidate.id}`);
                }
              }}
            />
          </div>
          <p>
            Updated on{' '}
            {new Date(candidate.updatedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </S.CandidateListItemHeadingButtons>
      )}
    </S.CandidateListItem>
  );
};

export default JobPostsListItem;
